<template>
  <modal v-on:cancel="closeModal">
    <template v-slot:title>
      <p class="modal-card-title">Update Holiday</p>
    </template>

    <template v-slot:body>
      <fieldset>
        <div class="field">
          <n-text-input
            ref="nameInput"
            v-model="name"
            label="Name"
            id="name"
            @input="$v.name.$touch()"
            :errorMessage="getErrorMessage($v, 'name', 'Name')"
          />
        </div>

        <div class="form-item">
          <label>
            On
            <n-tooltip
              position="right"
              text="Previously added holidays now in the past will automatically be removed"
            />
          </label>
        </div>

        <ul>
          <transition-group name="fade">
            <holiday-date-input
              v-for="(d, index) in dates"
              :key="d.key"
              v-model="dates[index].date"
              @update:model-value="$v.dates.$touch()"
              @delete="deleteDate(index)"
              ref="dateInputs"
            />
          </transition-group>

          <li class="date-list-item add-item-wrapper">
            <n-button variant="ghost" icon="add" :disabled="updateInProgress" @click="addDate"> Add Date </n-button>
            <p class="help is-danger" v-if="!$v.dates.required">You must add at least one date</p>
          </li>
        </ul>
      </fieldset>
    </template>

    <template v-slot:buttons>
      <n-button-group>
        <n-button variant="primary" :disabled="$v.$invalid" :loading="updateInProgress" @click="update">
          Update Holiday
        </n-button>
        <n-button :disabled="updateInProgress" @click="closeModal">Cancel</n-button>
      </n-button-group>
    </template>
  </modal>
</template>

<script lang="ts" setup>
import Toast from '@/shared/services/toast';
import Modal from '@/shared/components/Modal.vue';
import NTextInput from '@/shared/designsystem/components/input/text-input/text-input.vue';
import { UPDATE_HOLIDAY } from '@/store/holidays/actionTypes';
import { HolidayViewModel } from '@/features/bots/models/holidayViewModel';
import VuelidateUtil from '@/shared/util/vuelidateUtil';
import { nextTick, onMounted, PropType, ref } from 'vue';
import { useStore } from 'vuex';
import useVuelidate from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import HolidayDateInput from '@/features/settings/components/HolidayDateInput.vue';

const store = useStore();
const updateHoliday = (request: HolidayViewModel): Promise<HolidayViewModel> => store.dispatch(UPDATE_HOLIDAY, request);

const props = defineProps({
  holiday: {
    type: Object as PropType<HolidayViewModel>,
    required: true,
  },
});

const name = ref('');
const dates = ref<any[]>([]);
const updateInProgress = ref(false);

const nameInput = ref<InstanceType<typeof NTextInput> | null>(null);
const dateInputs = ref<InstanceType<typeof HolidayDateInput>[]>([]);

const emit = defineEmits<{
  (e: 'update', holiday: HolidayViewModel): void;
  (e: 'cancel'): void;
}>();

const validations = {
  name: {
    required,
    maxLength: maxLength(50),
    holidayNameValid: VuelidateUtil.objectName,
  },
  dates: {
    required,
  },
};

const $v = useVuelidate(validations, { name, dates });

onMounted(() => {
  name.value = props.holiday.name;
  if (props.holiday.dates) {
    dates.value = props.holiday.dates.map((d: Date, index: number) => ({
      date: d,
      key: index,
    }));
  } else {
    dates.value = [];
  }

  if (nameInput.value) {
    focusOnInput(nameInput.value);
  }
});

function closeModal() {
  emit('cancel');
}

function addDate() {
  dates.value.push({ date: new Date(), key: Date.now() });
  nextTick(() => {
    if (dateInputs.value) {
      const lastDateInput = dateInputs.value[dateInputs.value.length - 1];
      focusOnInput(lastDateInput);
    }
  });
}

function deleteDate(index: number) {
  dates.value.splice(index, 1);
}

function getErrorMessage(prop: 'name' | 'dates', label: string): string | null {
  return VuelidateUtil.getErrorMessage($v, prop, label, (failedRule) => {
    if (prop === 'name' && VuelidateUtil.isValidationInvalid($v, prop, 'holidayNameValid')) {
      return `${label} must only contain letters, numbers, spaces, and the characters , - _ ( ) &`;
    }
    return null;
  });
}

async function update() {
  if (updateInProgress.value) {
    return;
  }

  updateInProgress.value = true;
  try {
    const request = createRequestModel();
    const updatedHoliday = await updateHoliday(request);
    Toast.showSuccess('Holiday successfully updated');
    emit('update', updatedHoliday);
  } catch (err: any) {
    Toast.showError(err.message);
  } finally {
    updateInProgress.value = false;
  }
}

function createRequestModel(): HolidayViewModel {
  return {
    id: props.holiday.id,
    name: name.value,
    dates: dates.value.map((d) => d.date),
  };
}

function focusOnInput(component: InstanceType<typeof NTextInput> | InstanceType<typeof HolidayDateInput>) {
  if (component) {
    const inputElements = component.$el.getElementsByTagName('input');
    if (inputElements.length === 1) {
      inputElements[0].focus();
    }
  }
}
</script>

<style scoped lang="scss">
.date-list-item {
  padding: 0.25rem 0;
}

.date-item {
  display: flex;
}

.icon-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;

  .trash-icon {
    width: 1.25em;
    height: 1.25em;
    fill: #c7cdd3;
    margin-left: 0.5em;
    cursor: pointer;

    &:hover {
      fill: #a4a6a9;
    }
  }
}
</style>
